/* =========================================================
   Eternal Rose — Shared front-end JS
   Self-initializes on DOMContentLoaded and re-runs on Shopify
   theme-editor "shopify:section:load" events so each section
   becomes interactive whether it loads on first paint or is
   re-rendered live in the theme editor.
   ========================================================= */
(function () {
  'use strict';

  /* ---------- Hero: rotating headline + countdown ---------- */
  function initHero(root) {
    // Rotating phrases
    const rotator = root.querySelector('.rotator');
    if (rotator) {
      const lines = rotator.querySelectorAll('.rot-line');
      if (lines.length > 1) {
        let idx = 0;
        // Make sure first one is visible
        lines[0].classList.add('on');
        const interval = setInterval(() => {
          lines[idx].classList.remove('on');
          idx = (idx + 1) % lines.length;
          lines[idx].classList.add('on');
        }, 3200);
        // Track interval so we can clean up if the section is reloaded
        root._erRotatorInterval = interval;
      }
    }
    // Countdown to a target date (ISO in data-target)
    const cd = root.querySelector('[data-er-countdown]');
    if (cd) {
      const target = new Date(cd.getAttribute('data-target')).getTime();
      const dEl = cd.querySelector('[data-d]');
      const hEl = cd.querySelector('[data-h]');
      const mEl = cd.querySelector('[data-m]');
      const tick = () => {
        const diff = Math.max(0, target - Date.now());
        const days = Math.floor(diff / 86400000);
        const hours = Math.floor((diff / 3600000) % 24);
        const mins = Math.floor((diff / 60000) % 60);
        if (dEl) dEl.textContent = days;
        if (hEl) hEl.textContent = hours;
        if (mEl) mEl.textContent = mins;
      };
      tick();
      const id = setInterval(tick, 30000);
      root._erCountdownInterval = id;
    }
  }

  /* ---------- Parallax: editorial scroll stages ---------- */
  function initParallax(root) {
    const stage = root.querySelector('.px-stage');
    if (!stage) return;
    const photos = root.querySelectorAll('.px-photo');
    const articles = root.querySelectorAll('.px-article');
    const pips = root.querySelectorAll('.px-top-pip');
    const pctEl = root.querySelector('.px-top-pct');
    const keep = root.querySelector('.px-keep');
    const keepText = root.querySelector('.px-keep-text');
    const total = photos.length;
    if (!total) return;

    const update = () => {
      const r = root.getBoundingClientRect();
      const vh = window.innerHeight;
      const totalScroll = r.height + vh * 0.4;
      const scrolled = vh - r.top;
      const p = Math.max(0, Math.min(1, scrolled / totalScroll));
      const rawStage = p * total;
      const sIdx = Math.min(total - 1, Math.floor(rawStage));
      const t = rawStage - sIdx;

      photos.forEach((el, i) => {
        el.classList.remove('is-active', 'is-past', 'is-future');
        if (i === sIdx) el.classList.add('is-active');
        else if (i < sIdx) el.classList.add('is-past');
        else el.classList.add('is-future');
        el.style.zIndex = i === sIdx ? 3 : 1;
        const photoY = (p - 0.5) * 80;
        const scale = i === sIdx ? (1.05 + p * 0.08) : 1.05;
        const img = el.querySelector('img');
        if (img) img.style.transform = `translate3d(0, ${photoY}px, 0) scale(${scale})`;
      });
      articles.forEach((el, i) => {
        el.classList.toggle('on', i === sIdx);
      });
      pips.forEach((el, i) => {
        el.classList.remove('on', 'done');
        if (i === sIdx) el.classList.add('on');
        else if (i < sIdx) el.classList.add('done');
        const bar = el.querySelector('.px-top-pip-bar > span');
        if (bar) {
          const sx = i < sIdx ? 1 : i === sIdx ? t : 0;
          bar.style.transform = `scaleX(${sx})`;
        }
      });
      if (pctEl) {
        pctEl.firstChild && (pctEl.childNodes[0].nodeValue = String(Math.round(p * 100)).padStart(2, '0'));
      }
      if (keep) keep.classList.toggle('out', p > 0.92);
      if (keepText) {
        keepText.textContent = p < 0.05 ? 'Scroll to begin'
          : p < 0.5 ? 'Keep scrolling'
          : p < 0.85 ? 'Almost there'
          : 'One more';
      }
    };

    update();
    const onScroll = () => requestAnimationFrame(update);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', update);
    root._erParallaxCleanup = () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', update);
    };
  }

  /* ---------- FAQ accordion ---------- */
  function initFaq(root) {
    const items = root.querySelectorAll('.faq-item');
    items.forEach((item) => {
      const btn = item.querySelector('.faq-q');
      if (!btn) return;
      btn.addEventListener('click', () => {
        const wasOpen = item.classList.contains('open');
        // Close all
        items.forEach((i) => i.classList.remove('open'));
        // Open clicked, unless it was already open
        if (!wasOpen) item.classList.add('open');
      });
    });
  }

  /* ---------- Product card swatches ---------- */
  function initProductCard(root) {
    const cards = root.querySelectorAll('[data-er-pcard]');
    cards.forEach((card) => {
      const swatches = card.querySelectorAll('.sw[data-img]');
      const photo = card.querySelector('.pcard-photo');
      const label = card.querySelector('.pcard-sw-label');
      swatches.forEach((sw) => {
        sw.addEventListener('click', (e) => {
          e.stopPropagation();
          e.preventDefault();
          const img = sw.getAttribute('data-img');
          const lbl = sw.getAttribute('data-label') || '';
          const sold = sw.getAttribute('data-sold') === 'true';
          if (img && photo) photo.src = img;
          if (label) label.textContent = lbl + (sold ? ' · Sold out' : '');
          swatches.forEach((s) => s.classList.remove('on'));
          sw.classList.add('on');
        });
      });
    });
  }

  /* ---------- Sticky shop bar visibility ---------- */
  function initStickyBar(root) {
    const bar = root.querySelector('.shop-sticky');
    const closeBtn = root.querySelector('.shop-sticky-close');
    if (!bar) return;
    let dismissed = false;
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        dismissed = true;
        bar.classList.remove('visible');
      });
    }

    // Hooks: try to find a hero, the product collection, and a cta-band ANYWHERE on the page
    // (these are siblings of this section, not children, so we query document)
    const update = () => {
      if (dismissed) {
        bar.classList.remove('visible');
        return;
      }
      const hero = document.querySelector('.er-page .hero');
      const collection = document.querySelector('.er-page #collection, .er-page .collection');
      const ctaBand = document.querySelector('.er-page .cta-band');
      const vh = window.innerHeight;
      let pastHero = true;
      if (hero) {
        pastHero = hero.getBoundingClientRect().bottom < 200;
      }
      let inCollection = false;
      if (collection) {
        const r = collection.getBoundingClientRect();
        inCollection = r.top < vh * 0.6 && r.bottom > vh * 0.4;
      }
      let atCta = false;
      if (ctaBand) {
        atCta = ctaBand.getBoundingClientRect().top < vh * 0.7;
      }
      bar.classList.toggle('visible', pastHero && !inCollection && !atCta);
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    root._erStickyCleanup = () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }

  /* ---------- Reveal-on-scroll ---------- */
  function initReveal(root) {
    const els = root.querySelectorAll('.reveal');
    if (!els.length) return;
    if (!('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('in'));
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('in'); }),
      { threshold: 0.12 }
    );
    els.forEach((el) => obs.observe(el));
    root._erRevealObs = obs;
  }

  /* ---------- Master init: dispatch by section type via data-er ---------- */
  function init(root) {
    if (!root) return;
    const type = root.getAttribute('data-er');
    if (type === 'hero') initHero(root);
    else if (type === 'parallax') initParallax(root);
    else if (type === 'faq') initFaq(root);
    else if (type === 'product-twin' || type === 'collection') initProductCard(root);
    else if (type === 'shop-sticky') initStickyBar(root);
    initReveal(root);
  }

  function initAll(scope) {
    const elements = (scope || document).querySelectorAll('[data-er]');
    elements.forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document));
  } else {
    initAll(document);
  }

  // Theme editor: re-init when a section is loaded
  document.addEventListener('shopify:section:load', (e) => {
    const root = e.target.querySelector('[data-er]') || (e.target.matches && e.target.matches('[data-er]') ? e.target : null);
    if (root) init(root);
    else initAll(e.target);
  });
  document.addEventListener('shopify:section:unload', (e) => {
    const root = e.target.querySelector('[data-er]');
    if (!root) return;
    if (root._erRotatorInterval) clearInterval(root._erRotatorInterval);
    if (root._erCountdownInterval) clearInterval(root._erCountdownInterval);
    if (root._erParallaxCleanup) root._erParallaxCleanup();
    if (root._erStickyCleanup) root._erStickyCleanup();
    if (root._erRevealObs) root._erRevealObs.disconnect();
  });
})();
